import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Circle,
  XCircle,
  Trash2,
  Rocket,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  Terminal,
} from 'lucide-react';
import clsx from 'clsx';
import { baseStacks } from '../api';
import type { PreflightResult, ServerBaseStack, ServerStackVariable, DeployResult } from '../api';
import { useDateFormat } from '../lib/dates';

/**
 * Rolls the shared infrastructure stacks out to one server, and shows what is
 * already on it.
 *
 * Deploying runs `docker compose` over SSH on a machine that may belong to a
 * customer, so nothing here happens implicitly: the pre-flight veto is shown
 * before it can be overridden, and removal keeps the volumes (the proxy's
 * certificates and its database live in them).
 */

/** Only values the operator actually typed. An empty string is not an override — the
 *  backend would reject it, since compose substitutes it silently. */
function typedOverrides(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value.trim()) out[key] = value;
  }
  return out;
}

function apiMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (err instanceof Error ? err.message : fallback)
  );
}

const SOURCE_LABEL: Record<ServerStackVariable['source'], string> = {
  AUTO: 'abgeleitet',
  GENERATE: 'generiert',
  GLOBAL: 'global',
  PER_SERVER: 'pro Server',
  // Nicht „abgeleitet": das steht fuer einen Wert, der einmal entsteht und
  // dann bleibt. Dieser entsteht bei JEDEM Ausrollen neu, und genau darauf
  // kommt es an — eine neue Instanz ist dadurch von selbst dabei.
  BERECHNET: 'beim Ausrollen',
};

function PreflightIssues({ check, title }: { check: PreflightResult; title: string }) {
  const errors = check.issues.filter((i) => i.level === 'error');
  const warnings = check.issues.filter((i) => i.level === 'warning');

  if (!check.issues.length) {
    return (
      <div className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
        <ShieldCheck size={13} /> {title}: keine Einwände. Ports frei, Netzwerke vorhanden.
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'rounded-lg border p-3',
        errors.length ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
      )}
    >
      <div
        className={clsx(
          'flex items-center gap-1.5 text-xs font-medium',
          errors.length ? 'text-red-900' : 'text-amber-900',
        )}
      >
        <AlertTriangle size={13} /> {title}
      </div>
      <ul className="mt-1.5 space-y-1">
        {[...errors, ...warnings].map((issue) => (
          <li
            key={issue.message}
            className={clsx(
              'text-xs leading-snug',
              issue.level === 'error' ? 'text-red-800' : 'text-amber-800',
            )}
          >
            <span className="font-medium">
              {issue.level === 'error' ? 'Blocker: ' : 'Hinweis: '}
            </span>
            {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeployLog({ log }: { log: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
      >
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <Terminal size={12} /> Ausgabe des letzten Versuchs
      </button>
      {open && (
        <pre className="mt-1.5 max-h-64 overflow-auto rounded-lg bg-konsole px-3 py-2 font-mono text-[11px] leading-relaxed text-konsole-hell">
          {log}
        </pre>
      )}
    </div>
  );
}

function VariableRow({
  variable,
  value,
  onChange,
}: {
  variable: ServerStackVariable;
  value: string;
  onChange: (next: string) => void;
}) {
  const missing = !variable.resolvable && !value.trim();

  /** What the host gets if this field stays empty. */
  const placeholder = variable.secret
    ? variable.overridden
      ? '•••••••• gespeichert, leer lassen behält den Wert'
      : variable.resolvable
        ? '•••••••• wird generiert'
        : 'Wert erforderlich'
    : variable.suggestion !== null
      ? variable.suggestion
      : variable.resolvable
        ? 'wird beim Ausrollen generiert'
        : 'Wert erforderlich';

  return (
    <tr className={clsx(missing && 'bg-red-50/60')}>
      <td className="px-3 py-2 align-top">
        <div className="font-mono text-xs font-medium text-slate-800">{variable.key}</div>
        {variable.label && <div className="text-[11px] text-slate-500">{variable.label}</div>}
        {variable.description && (
          <div className="mt-0.5 max-w-56 text-[11px] leading-snug text-slate-400">
            {variable.description}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-wrap items-center gap-1">
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
            {SOURCE_LABEL[variable.source]}
          </span>
          {variable.secret && (
            <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[11px] text-surface">
              geheim
            </span>
          )}
          {variable.overridden && (
            <span
              className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-700"
              title="Für diesen Host wurde ein eigener Wert gespeichert; die Ableitung greift hier nicht."
            >
              überschrieben
            </span>
          )}
          {missing && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[11px] font-medium text-red-700">
              fehlt
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <input
          type={variable.secret ? 'password' : 'text'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className={clsx(
            'w-full rounded-lg border px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-2',
            missing
              ? 'border-red-300 focus:ring-red-400'
              : 'border-slate-300 focus:ring-brand-500',
          )}
        />
      </td>
    </tr>
  );
}

function StackCard({
  stack,
  serverId,
  hasSshKey,
  onChanged,
}: {
  stack: ServerBaseStack;
  serverId: string;
  hasSshKey: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { relative, dateTime } = useDateFormat();
  const needsInput = stack.variables.some((v) => !v.resolvable);
  const [showVariables, setShowVariables] = React.useState(needsInput);
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [result, setResult] = React.useState<DeployResult | null>(null);
  /** A pre-flight the operator asked for, versus one that vetoed a deploy. */
  const [check, setCheck] = React.useState<PreflightResult | null>(null);
  const [blocked, setBlocked] = React.useState<PreflightResult | null>(null);
  const [error, setError] = React.useState('');

  const deployMutation = useMutation({
    mutationFn: (force: boolean) =>
      baseStacks.deploy(stack.id, serverId, typedOverrides(values), force),
    onMutate: () => {
      setError('');
      setResult(null);
      setBlocked(null);
      setCheck(null);
    },
    onSuccess: (deployResult) => {
      setResult(deployResult);
      // Typed secrets are stored now; keeping them in component state would put
      // them back on the wire with every later action.
      setValues({});
      onChanged();
    },
    onError: (err: unknown) => {
      const response = (err as {
        response?: { status?: number; data?: { message?: string; preflight?: PreflightResult } };
      }).response;
      if (response?.status === 409 && response.data?.preflight) {
        setBlocked(response.data.preflight);
        return;
      }
      setError(apiMessage(err, 'Ausrollen fehlgeschlagen.'));
    },
  });

  const checkMutation = useMutation({
    mutationFn: () => baseStacks.preflight(stack.id, serverId),
    onSuccess: (preflight) => {
      setCheck(preflight);
      setBlocked(null);
      setError('');
    },
    onError: (err: unknown) => setError(apiMessage(err, 'Prüfung fehlgeschlagen.')),
  });

  const removeMutation = useMutation({
    mutationFn: () => baseStacks.undeploy(stack.id, serverId),
    onSuccess: (removeResult) => {
      setResult(removeResult);
      setError(removeResult.ok ? '' : removeResult.error ?? 'Entfernen fehlgeschlagen.');
      onChanged();
    },
    onError: (err: unknown) => setError(apiMessage(err, 'Entfernen fehlgeschlagen.')),
  });

  const busy = deployMutation.isPending || removeMutation.isPending || checkMutation.isPending;
  const deployment = stack.deployment;
  const isUp = deployment?.status === 'ACTIVE';
  const missing = stack.variables.filter((v) => !v.resolvable && !values[v.key]?.trim());
  const log = result?.log ?? deployment?.lastLog ?? '';

  const state: { icon: typeof Circle; tone: string; text: string } = !deployment
    ? { icon: Circle, tone: 'text-slate-400', text: 'Nicht ausgerollt' }
    : deployment.status === 'ACTIVE'
      ? {
          icon: CheckCircle2,
          tone: 'text-emerald-600',
          text: `Aktiv · ausgerollt ${relative(deployment.lastDeployedAt)}${
            deployment.deployedVersion ? ` (Version ${deployment.deployedVersion})` : ''
          }`,
        }
      : deployment.status === 'DEPLOYING'
        ? { icon: Loader2, tone: 'text-amber-600', text: 'Wird ausgerollt…' }
        : deployment.status === 'FAILED'
          ? { icon: XCircle, tone: 'text-red-600', text: 'Letzter Versuch fehlgeschlagen' }
          : deployment.status === 'REMOVED'
            ? {
                icon: Circle,
                tone: 'text-slate-400',
                text: `Entfernt${
                  deployment.lastDeployedAt ? ` · zuletzt aktiv ${dateTime(deployment.lastDeployedAt)}` : ''
                }`,
              }
            : { icon: Circle, tone: 'text-slate-400', text: 'Angelegt, noch nicht ausgerollt' };

  return (
    <div className="rounded-xl border border-slate-200 bg-surface">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-slate-900">{stack.name}</h3>
            <span className="font-mono text-[11px] text-slate-400">{stack.slug}</span>
            {stack.isDefault && (
              <span
                className="rounded bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-700"
                title={t('baseStacks.tickedByDefault')}
              >
                Standard
              </span>
            )}
            {stack.status === 'DRAFT' && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                Entwurf
              </span>
            )}
          </div>
          {stack.description && (
            <p className="mt-0.5 max-w-2xl text-xs leading-snug text-slate-500">
              {stack.description}
            </p>
          )}
          <div className={clsx('mt-1.5 flex items-center gap-1.5 text-xs font-medium', state.tone)}>
            <state.icon
              size={13}
              className={clsx('shrink-0', deployment?.status === 'DEPLOYING' && 'animate-spin')}
            />
            {state.text}
          </div>
          {deployment?.status === 'DEPLOYING' && (
            <p className="mt-1 text-[11px] leading-snug text-amber-700">
              Läuft noch oder ist beim Ausrollen abgebrochen (etwa weil die Verbindung
              abriss, während Images gezogen wurden). Aktualisieren zeigt den Endstand;
              erneutes Ausrollen ist gefahrlos, weil die generierten Passwörter
              wiederverwendet werden.
            </p>
          )}
          {deployment?.outdated && (
            <p className="mt-1 text-[11px] font-medium text-amber-700">
              Die Definition ist inzwischen bei Version {stack.version} — dieser Host
              läuft noch auf {deployment.deployedVersion}.
            </p>
          )}
          {deployment?.lastError && !result && (
            <p className="mt-1 max-w-2xl text-[11px] leading-snug text-red-600">
              {deployment.lastError}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <button
            onClick={() => checkMutation.mutate()}
            disabled={busy || !hasSshKey}
            title={hasSshKey ? 'Ports und Netzwerke prüfen, ohne etwas zu starten' : 'Kein SSH-Key hinterlegt'}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            {checkMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <ShieldCheck size={13} />
            )}
            Prüfen
          </button>
          <button
            onClick={() => deployMutation.mutate(false)}
            disabled={busy || !hasSshKey || missing.length > 0}
            title={
              !hasSshKey
                ? 'Kein SSH-Key hinterlegt'
                : missing.length
                  ? `Erst Werte für ${missing.map((v) => v.key).join(', ')} setzen`
                  : undefined
            }
            className="inline-flex items-center gap-1 rounded-lg bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            {deployMutation.isPending ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Rocket size={13} />
            )}
            {isUp ? 'Neu ausrollen' : 'Ausrollen'}
          </button>
          {deployment && deployment.status !== 'REMOVED' && (
            <button
              onClick={() => {
                if (
                  confirm(
                    `"${stack.name}" von diesem Host entfernen?\n\nDie Container werden gestoppt und entfernt. Volumes bleiben erhalten — Zertifikate und Proxy-Datenbank gehen also nicht verloren.`,
                  )
                )
                  removeMutation.mutate();
              }}
              disabled={busy}
              title={t('baseStacks.stopKeepVolumes')}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
            >
              {removeMutation.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2.5 border-t border-slate-100 px-4 py-3">
        {stack.declaredPorts.length > 0 && (
          <div className="text-[11px] text-slate-500">
            Belegt auf dem Host:{' '}
            <span className="font-mono text-slate-700">{stack.declaredPorts.join(', ')}</span>
          </div>
        )}

        {missing.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            <span className="font-medium">
              {missing.length === 1 ? 'Eine Variable hat keinen Wert' : `${missing.length} Variablen haben keinen Wert`}
              :{' '}
            </span>
            <span className="font-mono">{missing.map((v) => v.key).join(', ')}</span>. Compose
            würde dafür leere Zeichenketten einsetzen und trotzdem starten, deshalb ist das
            Ausrollen bis dahin gesperrt.
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            {error}
          </div>
        )}

        {check && <PreflightIssues check={check} title="Prüfung" />}

        {blocked && (
          <div className="space-y-2">
            <PreflightIssues check={blocked} title={t('baseStacks.hostNotReady')} />
            <button
              onClick={() => deployMutation.mutate(true)}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg border border-red-300 bg-surface px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              <AlertTriangle size={13} /> Trotzdem ausrollen
            </button>
          </div>
        )}

        {result && (
          <div
            className={clsx(
              'rounded-lg border px-3 py-2 text-xs',
              result.ok
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-800',
            )}
          >
            {result.ok ? (
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={13} /> Fertig. Der Stack läuft auf {stack.slug}.
              </span>
            ) : (
              <span className="flex items-start gap-1.5">
                <XCircle size={13} className="mt-0.5 shrink-0" />
                <span>{result.error ?? 'Fehlgeschlagen.'}</span>
              </span>
            )}
          </div>
        )}

        {stack.variables.length > 0 && (
          <div>
            <button
              onClick={() => setShowVariables((s) => !s)}
              className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800"
            >
              {showVariables ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {stack.variables.length} Variablen
              {stack.variables.some((v) => v.overridden) && (
                <span className="text-[11px] font-normal text-brand-600">
                  · {stack.variables.filter((v) => v.overridden).length} für diesen Host
                  überschrieben
                </span>
              )}
            </button>
            {showVariables && (
              <>
                <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        {[t('baseStacks.variable'), t('baseStacks.origin'), t('baseStacks.valueForHost')].map((h) => (
                          <th
                            key={h}
                            className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {stack.variables.map((variable) => (
                        <VariableRow
                          key={variable.key}
                          variable={variable}
                          value={values[variable.key] ?? ''}
                          onChange={(next) =>
                            setValues((v) => ({ ...v, [variable.key]: next }))
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1.5 text-[11px] leading-snug text-slate-400">
                  Leere Felder behalten den gezeigten Wert. Was hier eingetragen wird, gilt
                  ab dem nächsten Ausrollen nur für diesen Host — abgeleitete Werte sind ein
                  guter Vorschlag, keine Vorschrift. Generierte Passwörter werden bei jedem
                  weiteren Ausrollen wiederverwendet, sonst käme der Stack nicht mehr an
                  seine eigene Datenbank.
                </p>
              </>
            )}
          </div>
        )}

        {log && <DeployLog log={log} />}
      </div>
    </div>
  );
}

export default function BaseStackPanel({
  serverId,
  hostname,
  hasSshKey,
  onClose,
}: {
  serverId: string;
  hostname: string;
  hasSshKey: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['server-base-stacks', serverId],
    queryFn: () => baseStacks.forServer(serverId),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const stacks = data ?? [];
  const active = stacks.filter((s) => s.deployment?.status === 'ACTIVE').length;

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ['server-base-stacks', serverId] });
    // The container list on the same server just changed.
    void qc.invalidateQueries({ queryKey: ['server-docker', serverId] });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4">
      <div className="mt-8 flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Base-Stacks</h2>
            <p className="font-mono text-xs text-slate-500">{hostname}</p>
          </div>
          <div className="flex items-center gap-2">
            {stacks.length > 0 && (
              <span className="text-xs text-slate-400">
                {active} von {stacks.length} aktiv
              </span>
            )}
            <button
              onClick={() => void refetch()}
              disabled={isFetching}
              className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isFetching ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              Aktualisieren
            </button>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-6 py-5">
          {!hasSshKey && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-600" />
              <p className="text-xs leading-snug text-amber-800">
                Für diesen Server ist kein SSH-Key hinterlegt. Ausrollen und Prüfen brauchen
                eine Verbindung zum Host — erst einen Key hinterlegen oder generieren.
              </p>
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin" />
              Lade Base-Stacks…
            </div>
          )}

          {isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {apiMessage(error, 'Base-Stacks konnten nicht geladen werden.')}
            </div>
          )}

          {data && stacks.length === 0 && (
            <p className="rounded-lg border border-slate-200 bg-surface px-3 py-8 text-center text-sm text-slate-500">
              Es sind noch keine Base-Stacks angelegt. Unter PC-Einstellungen → Base-Stacks
              lassen sich Composes zentral hinterlegen.
            </p>
          )}

          {stacks.map((stack) => (
            <StackCard
              key={stack.id}
              stack={stack}
              serverId={serverId}
              hasSshKey={hasSshKey}
              onChanged={invalidate}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
